import React from 'react';
import { Row, Col, Card, Tag, Alert, Typography, Divider } from 'antd';
import { 
  ClockCircleOutlined, 
  SafetyCertificateOutlined,
  DollarOutlined,
  GlobalOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  StarOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';

const { Title, Paragraph, Text } = Typography;

const BlogSection: React.FC = () => {
  return (
    <section id="blog" style={{ 
      padding: '100px 0', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* 背景装饰 */}
      <div style={{
        position: 'absolute',
        top: '-50px',
        right: '0%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(0, 113, 227, 0.08) 0%, transparent 70%)',
        borderRadius: '50%'
      }} />
      
      <div className="container-wide">
        {/* 标题区域 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: 'center', marginBottom: '80px' }}
        >
          <Title level={2} style={{ 
            color: '#1D1D1F', 
            fontSize: '2.5rem',
            marginBottom: '16px'
          }}>
            🚀 行业资讯
          </Title>
          <Paragraph style={{ 
            fontSize: '1.2rem', 
            color: '#86868B',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            深度解析美国空运专线行业，为您提供最新市场趋势和专业指导
          </Paragraph>
        </motion.div>

        {/* 文章展示区域 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Card
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: 'none',
              borderRadius: '24px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              marginBottom: '40px'
            }}
            bodyStyle={{ padding: '60px' }}
          >
            {/* 文章标题和元信息 */}
            <div style={{ marginBottom: '40px' }}>
              <Title level={1} style={{ 
                color: '#1D1D1F', 
                fontSize: '2.2rem', 
                marginBottom: '20px',
                lineHeight: '1.2'
              }}>
                2024年美国空运专线选择指南：如何选择最适合的跨境物流服务
              </Title>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '20px',
                marginBottom: '24px',
                flexWrap: 'wrap'
              }}>
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  目标关键词：美国空运专线、跨境物流、国际空运
                </Tag>
                <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  字数：1500+
                </Tag>
                <Tag color="orange" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  阅读时长：6-8分钟
                </Tag>
              </div>
            </div>

            <Divider />

            {/* 痛点分析 */}
            <div style={{ marginBottom: '50px' }}>
              <Title level={3} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                color: '#1D1D1F' 
              }}>
                🎯 受众痛点分析
              </Title>
              
              <Title level={4} style={{ color: '#333', marginTop: '24px' }}>
                跨境电商卖家面临的核心问题：
              </Title>
              
              <Row gutter={[24, 16]}>
                {[
                  { icon: '❌', text: '海运太慢，旺季来不及补货' },
                  { icon: '❌', text: '快递太贵，压缩利润空间' },
                  { icon: '❌', text: '清关复杂，货物经常被扣' },
                  { icon: '❌', text: '服务商众多，不知如何选择' }
                ].map((item, index) => (
                  <Col xs={24} md={12} key={index}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      padding: '16px',
                      background: '#fff5f5',
                      borderRadius: '12px',
                      border: '1px solid #ffebee'
                    }}>
                      <span style={{ fontSize: '18px' }}>{item.icon}</span>
                      <Text style={{ fontSize: '16px' }}>{item.text}</Text>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>

            <Divider />

            {/* 市场数据 */}
            <div style={{ marginBottom: '50px' }}>
              <Title level={3} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                color: '#1D1D1F' 
              }}>
                📊 2024年美国空运专线市场数据
              </Title>
              
              <Title level={4} style={{ color: '#333', marginTop: '24px' }}>
                市场现状分析
              </Title>
              
              <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
                {[
                  { title: '2000亿美元', desc: '中美空运贸易额', icon: <DollarOutlined /> },
                  { title: '78%', desc: '卖家要求7天内到货', icon: <ClockCircleOutlined /> },
                  { title: '30-40%', desc: '空运比快递节省成本', icon: <StarOutlined /> },
                  { title: '日趋严格', desc: 'FDA、FCC认证要求', icon: <SafetyCertificateOutlined /> }
                ].map((stat, index) => (
                  <Col xs={12} md={6} key={index}>
                    <Card 
                      size="small" 
                      style={{ 
                        textAlign: 'center', 
                        background: '#f8f9fa',
                        border: '1px solid #e9ecef'
                      }}
                    >
                      <div style={{ color: '#0071e3', fontSize: '24px', marginBottom: '8px' }}>
                        {stat.icon}
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1D1D1F', marginBottom: '4px' }}>
                          {stat.title}
                        </div>
                        <div style={{ fontSize: '14px', color: '#86868B' }}>
                          {stat.desc}
                        </div>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>

            <Divider />

            {/* 5大核心要素 */}
            <div style={{ marginBottom: '50px' }}>
              <Title level={3} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                color: '#1D1D1F' 
              }}>
                🔍 美国空运专线选择的5大核心要素
              </Title>
              
              <Row gutter={[24, 32]}>
                {[
                  {
                    icon: <ClockCircleOutlined />,
                    title: '1. 时效稳定性',
                    tips: [
                      '选择承诺5-8天时效的美国空运专线服务商',
                      '确认是否提供超时赔付保障',
                      '查看历史准点率数据（建议95%以上）'
                    ],
                    highlight: '318空派数据：97.2%的货物在承诺时效内送达'
                  },
                  {
                    icon: <SafetyCertificateOutlined />,
                    title: '2. 清关专业度',
                    tips: [
                      '是否在美国四大清关机场有专业团队',
                      '是否提供关税代付服务',
                      '特殊产品（FDA、FCC）清关经验',
                      '清关成功率是否达到95%以上'
                    ],
                    highlight: '实际案例：深圳某电子产品卖家，通过318空派发货1000台蓝牙音响到美国，全部通过FCC认证清关，清关时效仅24小时。'
                  },
                  {
                    icon: <DollarOutlined />,
                    title: '3. 成本控制',
                    tips: [
                      '选择包含关税代付的DDP服务',
                      '优选体积重计费有优势的服务商',
                      '考虑拼箱服务降低小批量成本'
                    ],
                    highlight: '总成本 = 基础运费 + 燃油附加费 + 清关费用 + 关税代付 + 末端配送'
                  },
                  {
                    icon: <GlobalOutlined />,
                    title: '4. 服务覆盖范围',
                    tips: [
                      '是否覆盖全美50州',
                      '偏远地区是否有额外费用',
                      '是否支持直送亚马逊FBA仓库',
                      '末端配送时效是否稳定'
                    ]
                  },
                  {
                    icon: <ToolOutlined />,
                    title: '5. 增值服务能力',
                    tips: [
                      '免费仓储服务（7-15天）',
                      '货物包装加固',
                      '保险服务覆盖',
                      '全程轨迹跟踪',
                      '24/7客服响应'
                    ]
                  }
                ].map((item, index) => (
                  <Col xs={24} lg={index < 2 ? 12 : 24} key={index}>
                    <Card 
                      style={{ 
                        height: '100%',
                        background: '#ffffff',
                        border: '1px solid #f0f0f0'
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        marginBottom: '16px'
                      }}>
                        <div style={{ color: '#0071e3', fontSize: '20px' }}>
                          {item.icon}
                        </div>
                        <Title level={4} style={{ margin: 0, color: '#1D1D1F' }}>
                          {item.title}
                        </Title>
                      </div>
                      
                      <div style={{ marginBottom: '16px' }}>
                        {item.tips.map((tip, tipIndex) => (
                          <div key={tipIndex} style={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            gap: '8px',
                            marginBottom: '8px'
                          }}>
                            <CheckCircleOutlined style={{ color: '#52c41a', marginTop: '4px' }} />
                            <Text>{tip}</Text>
                          </div>
                        ))}
                      </div>
                      
                      {item.highlight && (
                        <Alert
                          message="专业数据"
                          description={item.highlight}
                          type="info"
                          showIcon
                          style={{ fontSize: '14px' }}
                        />
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>

            <Divider />

            {/* 318空派推荐 */}
            <div style={{ marginBottom: '40px' }}>
              <Title level={3} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                color: '#1D1D1F' 
              }}>
                🏆 2024年美国空运专线服务商推荐
              </Title>
              
              <Card 
                title={<span style={{ fontSize: '18px', fontWeight: 'bold' }}>推荐理由：318空派专线</span>}
                style={{ 
                  background: '#1d1d1f',
                  border: '1px solid #424245',
                  color: '#f5f5f7'
                }}
                headStyle={{ 
                  background: 'transparent', 
                  color: '#f5f5f7',
                  borderBottom: '1px solid rgba(255,255,255,0.12)'
                }}
                bodyStyle={{ background: 'transparent' }}
              >
                <Row gutter={[16, 16]}>
                  {[
                    { label: '时效保障', desc: '5-8天稳定时效，超时赔付' },
                    { label: '清关优势', desc: '四大机场专业清关，97.2%成功率' },
                    { label: '成本优化', desc: '0.5kg起收，比快递节省40%' },
                    { label: '服务覆盖', desc: '全美50州，支持FBA直送' },
                    { label: '专业团队', desc: '10年行业经验，10万+企业信赖' }
                  ].map((advantage, index) => (
                    <Col xs={24} md={12} lg={24/5} key={index}>
                      <div style={{ textAlign: 'center', color: '#fff' }}>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}>
                          ⭐ {advantage.label}
                        </div>
                        <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px' }}>
                          {advantage.desc}
                        </Text>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card>
            </div>

            {/* CTA区域 */}
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 0',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              borderRadius: '16px',
              marginTop: '40px'
            }}>
              <Title level={3} style={{ color: '#1D1D1F', marginBottom: '20px' }}>
                🔥 立即行动 - 获取专属优惠
              </Title>
              
              <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                {[
                  '🎁 新客户首单立减100元',
                  '🎁 免费提供清关咨询服务',
                  '🎁 7天免费仓储服务',
                  '🎁 专属客服一对一服务'
                ].map((offer, index) => (
                  <Col xs={12} md={6} key={index}>
                    <Text style={{ 
                      display: 'block', 
                      fontSize: '14px', 
                      color: '#333',
                      padding: '8px',
                      background: '#fff',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      {offer}
                    </Text>
                  </Col>
                ))}
              </Row>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <a href="#pricing" style={{ textDecoration: 'none' }}>
                  <div style={{
                    background: '#0071e3',
                    color: '#fff',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}>
                    🖥️ 智能计算运费
                  </div>
                </a>
                <a href="#contact" style={{ textDecoration: 'none' }}>
                  <div style={{
                    background: '#fff',
                    color: '#0071e3',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    border: '2px solid #0071e3',
                    transition: 'all 0.3s ease'
                  }}>
                    💬 立即咨询客服
                  </div>
                </a>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* 文章信息 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          style={{ textAlign: 'center', marginTop: '40px' }}
        >
          <Text style={{ color: '#86868B', fontSize: '14px' }}>
            本文由318空派物流专业团队撰写，10年行业经验，服务10万+跨境企业。如需转载请注明出处。
          </Text>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogSection;
